import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from '@utils/framerMotionProxy';
import { reviewsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

const ReviewModal = ({ onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    reviewText: '',
    rating: 5
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error('Размер файла не должен превышать 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('Можно загружать только изображения');
        return;
      }

      setImage(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.reviewText.trim().length < 10) {
      toast.error('Текст отзыва должен содержать минимум 10 символов');
      return;
    }

    if (formData.reviewText.length > 1000) {
      toast.error('Текст отзыва не должен превышать 1000 символов');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('reviewText', formData.reviewText.trim());
      submitData.append('rating', formData.rating);
      
      if (image) {
        submitData.append('image', image);
      }

      await reviewsAPI.createReview(submitData);
      
      toast.success('Отзыв отправлен на модерацию');
      onSubmitted();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Ошибка при отправке отзыва');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <button
        key={index}
        type="button"
        onClick={() => setFormData(prev => ({ ...prev, rating: index + 1 }))}
        className={`w-8 h-8 ${index < formData.rating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
      >
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </button>
    ));
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-6 relative">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 8 }}
          transition={{ duration: 0.25 }}
          className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        >
          <button
            aria-label="Close"
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>

          <div className="text-center mb-6">
            <h2 className="text-xl md:text-2xl lg:text-[1.8rem] uppercase font-bold text-gray-900 text-center">
              {t('reviews.addReview')}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('reviews.rating')}
              </label>
              <div className="flex items-center space-x-1">
                {renderStars()}
                <span className="ml-2 text-sm text-gray-600">
                  ({formData.rating} {formData.rating === 1 ? 'звезда' : formData.rating < 5 ? 'звезды' : 'звёзд'})
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="reviewText" className="block text-sm font-medium text-gray-700 mb-2">
                {t('reviews.reviewText')} *
              </label>
              <textarea
                id="reviewText"
                name="reviewText"
                rows={4}
                value={formData.reviewText}
                onChange={handleInputChange}
                placeholder={t('reviews.reviewTextPlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                maxLength={1000}
                required
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.reviewText.length}/1000
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('reviews.image')} ({t('reviews.optional')})
              </label>

              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="imageInput"
                  />
                  <label htmlFor="imageInput" className="cursor-pointer">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">
                        {t('reviews.uploadImage')}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG до 5MB
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center items-center px-4 py-3 border border-gray-200 text-sm font-medium rounded-lg text-gray-800 bg-white transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || formData.reviewText.trim().length < 10}
                className="w-full inline-flex justify-center items-center px-4 py-3 rounded-lg text-white font-medium transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#EC8C8E' }}
              >
                {isSubmitting ? t('common.loading') : t('reviews.submit')}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ReviewModal;
